import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Loader2, CheckCircle2, Heart, Star, Crown, Check, Plus } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader, Kicker } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { OWNER_EMAIL, SPONSORS } from "../lib/site";

const SPONSOR_TIERS = [
  {
    name: "Community Supporter",
    range: "$0 – $500",
    icon: Heart,
    featured: false,
    perks: [
      "A heartfelt thank-you for your donation",
      "The good feeling of supporting inclusion in West Niagara",
    ],
    cta: "Donate",
    to: "/sponsor-donation",
  },
  {
    name: "Community Sponsor",
    range: "$500 – $1,500",
    icon: Star,
    featured: true,
    perks: [
      "Your logo featured on our website",
      "Your logo on the official event t-shirt",
      "A shout-out on race day",
      "Featured across all of our social media posts",
    ],
    cta: "Donate $500",
    to: "/sponsor-donation?amount=500",
  },
  {
    name: "Presenting Sponsor",
    range: "$2,000+",
    icon: Crown,
    featured: false,
    perks: [
      "Everything in the Community Sponsor tier",
      "Named as our Major / Presenting Sponsor",
      "Mentioned everywhere the race is mentioned",
      "e.g. “Grimsby On The Lake Turkey Trot, presented by [Your Brand]”",
    ],
    cta: "Donate $2,000",
    to: "/sponsor-donation?amount=2000",
  },
];

const field = "w-full rounded-xl border border-cream/20 bg-white/10 px-4 py-3 text-cream placeholder:text-cream/50 outline-none focus:border-ochre focus:ring-2 focus:ring-ochre/30 transition-colors duration-200";

export default function Sponsors() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const scrollToForm = () => {
    const el = document.getElementById("become-a-sponsor");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#become-a-sponsor") {
      const t = setTimeout(scrollToForm, 400);
      return () => clearTimeout(t);
    }
  }, [location]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: `Sponsorship Inquiry, ${form.company || form.name}`,
        message: `Company/Organization: ${form.company}\n\n${form.message}`,
      });
      toast.success("Thanks! Your sponsorship inquiry has been sent.");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Sponsors" path="/sponsors" description="Become a sponsor of the Grimsby On The Lake Turkey Trot and support Inclusion West Niagara." />
      <PageHeader
        chapter="our partners"
        title="Sponsor the trot."
        sub="Local businesses are the wind at our backs. Partner with us to keep entry fees low, donations high, and inclusion front and centre."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-12 sm:pt-16" data-testid="our-sponsors">
        <Reveal>
          <Kicker>our 2026 sponsors</Kicker>
          <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
            The businesses powering the trot.
          </h2>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-6">
          {SPONSORS.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-3xl bg-white border border-black/5 shadow-warm px-10 py-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg"
                data-testid={`sponsor-card-${i}`}
              >
                <img src={s.logo} alt={s.name} className="h-16 sm:h-20 w-auto object-contain" />
                <div className="mt-6 flex items-center justify-between gap-8">
                  <div>
                    <p className="font-semibold">{s.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-terracotta">{s.tier}</p>
                  </div>
                  <ArrowUpRight size={20} className="shrink-0 text-ink-soft transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-terracotta" />
                </div>
              </a>
            </Reveal>
          ))}

          <Reveal delay={SPONSORS.length * 0.08}>
            <button
              onClick={scrollToForm}
              className="group block rounded-3xl border-2 border-dashed border-ink/20 px-10 py-8 text-left transition-all duration-300 hover:-translate-y-1 hover:border-terracotta"
              data-testid="sponsor-card-placeholder"
            >
              <div className="flex h-16 sm:h-20 items-center gap-2 text-ink-soft transition-colors duration-300 group-hover:text-terracotta">
                <Plus size={22} />
                <span className="font-display text-2xl font-semibold">Your logo here</span>
              </div>
              <div className="mt-6 flex items-center justify-between gap-8">
                <div>
                  <p className="font-semibold">Become a sponsor</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-terracotta">2026 Season</p>
                </div>
                <ArrowUpRight size={20} className="shrink-0 text-ink-soft transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-terracotta" />
              </div>
            </button>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10 sm:py-14">
        <Reveal>
          <Kicker>sponsorship tiers</Kicker>
          <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
            Ways to partner with us.
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-3 items-stretch">
          {SPONSOR_TIERS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Reveal key={t.name} delay={i * 0.08}>
                <div
                  className={`h-full flex flex-col rounded-3xl p-8 sm:p-10 border transition-all duration-300 hover:-translate-y-1 ${
                    t.featured
                      ? "bg-forest text-cream border-forest shadow-warm-lg"
                      : "bg-white text-ink border-black/5 shadow-warm"
                  }`}
                  data-testid={`sponsor-tier-${i}`}
                >
                  <Icon size={28} className={t.featured ? "text-ochre" : "text-terracotta"} />
                  <p className={`mt-5 text-xs uppercase tracking-[0.2em] ${t.featured ? "text-cream/60" : "text-ink-soft"}`}>
                    {t.range}
                  </p>
                  <h3 className="mt-1 font-display text-3xl font-semibold">{t.name}</h3>
                  <ul className="mt-6 space-y-2.5 text-sm flex-1">
                    {t.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <Check size={16} className={`mt-0.5 shrink-0 ${t.featured ? "text-ochre" : "text-forest"}`} />
                        <span className={t.featured ? "text-cream/85" : "text-ink-soft"}>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    {t.to.startsWith("#") ? (
                      <a
                        href={t.to}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-105 ${
                          t.featured ? "bg-ochre text-ink hover:bg-ochre/90" : "bg-terracotta text-white hover:bg-sienna"
                        }`}
                      >
                        {t.cta} <ArrowUpRight size={16} />
                      </a>
                    ) : (
                      <Link
                        to={t.to}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-terracotta text-white hover:bg-sienna transition-all duration-200 hover:scale-105"
                      >
                        {t.cta} <ArrowUpRight size={16} />
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section id="become-a-sponsor" className="mx-auto max-w-7xl px-5 sm:px-8 py-8 pb-24 sm:pb-32">
        <Reveal>
          <div className="rounded-3xl bg-forest text-cream px-8 py-14 sm:px-16 sm:py-20 grid gap-12 lg:grid-cols-2 items-start">
            <div>
              <Kicker className="text-ochre">become a sponsor</Kicker>
              <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl max-w-lg">
                Put your brand behind a great cause.
              </h2>
              <p className="mt-4 max-w-md text-cream/80 leading-relaxed">
                Sponsorship packages include shirt placement, banners at the finish line and social media
                shout-outs, plus the good feeling of funding inclusion in West Niagara. Send us a note and
                we'll get right back to you with our sponsor kit.
              </p>
              <a
                href={`mailto:${OWNER_EMAIL}`}
                className="mt-6 inline-flex items-center gap-2 text-ochre font-semibold hover:text-cream transition-colors duration-200"
                data-testid="sponsor-email-link"
              >
                <Mail size={18} /> {OWNER_EMAIL}
              </a>
            </div>

            {done ? (
              <div className="rounded-3xl bg-white/10 border border-cream/20 p-10 text-center" data-testid="sponsor-success">
                <CheckCircle2 size={44} className="mx-auto text-ochre" />
                <h3 className="mt-4 font-display text-2xl font-semibold">Inquiry sent!</h3>
                <p className="mt-2 text-cream/80">Thank you for your interest in sponsoring, we'll be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="sponsor-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Your name" value={form.name} onChange={set("name")} className={field} data-testid="sponsor-name" />
                  <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="sponsor-email" />
                </div>
                <input placeholder="Company / Organization" value={form.company} onChange={set("company")} className={field} data-testid="sponsor-company" />
                <textarea required placeholder="Tell us how you'd like to get involved…" rows={5} value={form.message} onChange={set("message")} className={field} data-testid="sponsor-message" />
                <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-sienna transition-colors duration-200 disabled:opacity-60" data-testid="sponsor-submit">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send sponsorship inquiry <ArrowUpRight size={16} /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </>
  );
}
